import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit2,
  Check,
  X,
  RefreshCw,
  CreditCard,
  Users,
  Building2,
  Percent,
} from 'lucide-react';
import {
  getPlans,
  createPlan,
  updatePlan,
  SubscriptionPlan,
  CreatePlanDto,
  formatCurrency,
  getStatusLabel,
} from '@/services/billingService';
import { ToastContainer, useToast } from '@/shared/components/ui/Toast';

export const SubscriptionPlansPage: React.FC = () => {
  const { toasts, removeToast } = useToast();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [formData, setFormData] = useState<CreatePlanDto>({
    name: '',
    description: '',
    pricingType: 'FIXED_FEE',
    basePrice: 0,
    pricePerUser: 0,
    billingCycle: 'MONTHLY',
    maxUsers: undefined,
    maxCampuses: undefined,
    features: {},
    tvaRate: 20,
    tvaExempt: false,
  });
  const [saving, setSaving] = useState(false);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPlans();
      setPlans(data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleCreate = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      description: '',
      pricingType: 'FIXED_FEE',
      basePrice: 0,
      pricePerUser: 0,
      billingCycle: 'MONTHLY',
      maxUsers: undefined,
      maxCampuses: undefined,
      features: {},
      tvaRate: 20,
      tvaExempt: false,
    });
    setShowModal(true);
  };

  const handleEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description || '',
      pricingType: plan.pricingType,
      basePrice: plan.basePrice,
      pricePerUser: plan.pricePerUser || 0,
      billingCycle: plan.billingCycle,
      maxUsers: plan.maxUsers || undefined,
      maxCampuses: plan.maxCampuses || undefined,
      features: plan.features,
      tvaRate: plan.tvaRate,
      tvaExempt: plan.tvaExempt,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (editingPlan) {
        await updatePlan(editingPlan.id, formData);
      } else {
        await createPlan(formData);
      }
      setShowModal(false);
      fetchPlans();
    } catch (error) {
      console.error('Failed to save plan:', error);
    } finally {
      setSaving(false);
    }
  };

  const togglePlanStatus = async (plan: SubscriptionPlan) => {
    try {
      await updatePlan(plan.id, { ...plan, isActive: !plan.isActive } as any);
      fetchPlans();
    } catch (error) {
      console.error('Failed to toggle plan status:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Plans d'abonnement</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gérez les plans tarifaires de la plateforme
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchPlans}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw size={16} />
            Actualiser
          </button>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            <Plus size={16} />
            Nouveau plan
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`bg-white rounded-xl shadow-sm border ${
              plan.isActive ? 'border-gray-100' : 'border-red-200 bg-red-50'
            } p-6`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                <p className="text-sm text-gray-500">{plan.description}</p>
              </div>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  plan.isActive
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {plan.isActive ? 'Actif' : 'Inactif'}
              </span>
            </div>

            {/* Price */}
            <div className="mb-4">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-gray-900">
                  {formatCurrency(plan.basePrice)}
                </span>
                <span className="text-gray-500">
                  /{getStatusLabel(plan.billingCycle).toLowerCase()}
                </span>
              </div>
              {(plan.pricingType === 'PER_USER' || plan.pricingType === 'HYBRID') && plan.pricePerUser && (
                <p className="text-sm text-gray-500 mt-1">
                  + {formatCurrency(plan.pricePerUser)} par utilisateur
                </p>
              )}
            </div>

            {/* Features */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CreditCard size={16} className="text-indigo-500" />
                <span>{getStatusLabel(plan.pricingType)}</span>
              </div>
              {plan.maxUsers && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Users size={16} className="text-indigo-500" />
                  <span>Max {plan.maxUsers} utilisateurs</span>
                </div>
              )}
              {plan.maxCampuses && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Building2 size={16} className="text-indigo-500" />
                  <span>Max {plan.maxCampuses} campus</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Percent size={16} className="text-indigo-500" />
                <span>TVA: {plan.tvaExempt ? 'Exonéré' : `${plan.tvaRate}%`}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
              <button
                onClick={() => handleEdit(plan)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <Edit2 size={14} />
                Modifier
              </button>
              <button
                onClick={() => togglePlanStatus(plan)}
                className={`flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg ${
                  plan.isActive
                    ? 'text-red-700 bg-red-100 hover:bg-red-200'
                    : 'text-green-700 bg-green-100 hover:bg-green-200'
                }`}
              >
                {plan.isActive ? <X size={14} /> : <Check size={14} />}
              </button>
            </div>
          </div>
        ))}

        {plans.length === 0 && (
          <div className="col-span-full text-center py-12">
            <CreditCard className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900">Aucun plan</h3>
            <p className="text-gray-500 mt-1">
              Créez votre premier plan d'abonnement
            </p>
            <button
              onClick={handleCreate}
              className="mt-4 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
            >
              Créer un plan
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingPlan ? 'Modifier le plan' : 'Nouveau plan'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du plan *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type de tarification *
                  </label>
                  <select
                    value={formData.pricingType}
                    onChange={(e) => {
                      const newType = e.target.value as 'FIXED_FEE' | 'PER_USER' | 'HYBRID';
                      setFormData({
                        ...formData,
                        pricingType: newType,
                        // Clear fields that don't apply to the new pricing type so stale
                        // values aren't silently saved on the plan.
                        basePrice: newType === 'PER_USER' ? 0 : formData.basePrice,
                        pricePerUser: newType === 'FIXED_FEE' ? 0 : formData.pricePerUser,
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="FIXED_FEE">Forfait fixe</option>
                    <option value="PER_USER">Par utilisateur</option>
                    <option value="HYBRID">Forfait fixe + par utilisateur</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cycle de facturation *
                  </label>
                  <select
                    value={formData.billingCycle}
                    onChange={(e) => setFormData({ ...formData, billingCycle: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="MONTHLY">Mensuel</option>
                    <option value="QUARTERLY">Trimestriel</option>
                    <option value="YEARLY">Annuel</option>
                  </select>
                </div>

                {(formData.pricingType === 'FIXED_FEE' || formData.pricingType === 'HYBRID') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prix de base (MAD) *
                    </label>
                    <input
                      type="number"
                      value={formData.basePrice}
                      onChange={(e) => setFormData({ ...formData, basePrice: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                )}

                {(formData.pricingType === 'PER_USER' || formData.pricingType === 'HYBRID') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prix par utilisateur (MAD) *
                    </label>
                    <input
                      type="number"
                      value={formData.pricePerUser}
                      onChange={(e) => setFormData({ ...formData, pricePerUser: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max utilisateurs
                  </label>
                  <input
                    type="number"
                    value={formData.maxUsers || ''}
                    onChange={(e) => setFormData({ ...formData, maxUsers: e.target.value ? parseInt(e.target.value) : undefined })}
                    min="1"
                    placeholder="Illimité"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max campus
                  </label>
                  <input
                    type="number"
                    value={formData.maxCampuses || ''}
                    onChange={(e) => setFormData({ ...formData, maxCampuses: e.target.value ? parseInt(e.target.value) : undefined })}
                    min="1"
                    placeholder="Illimité"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Taux TVA (%)
                  </label>
                  <input
                    type="number"
                    value={formData.tvaRate}
                    onChange={(e) => setFormData({ ...formData, tvaRate: parseFloat(e.target.value) || 20 })}
                    min="0"
                    max="100"
                    step="0.1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="tvaExempt"
                    checked={formData.tvaExempt}
                    onChange={(e) => setFormData({ ...formData, tvaExempt: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="tvaExempt" className="ml-2 text-sm text-gray-700">
                    Exonéré de TVA
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : editingPlan ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default SubscriptionPlansPage;
